const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: What is a Differential Equation? ───────────────────
function DiagramWhatIsODE() {
  const W = 900, H = 360;

  // Left panel: slope field for dy/dx = y (solution: y = Ce^x)
  const sfCx = 220, sfCy = 180, sfSc = 48;
  const sfTx = x => sfCx + x * sfSc;
  const sfTy = y => sfCy - y * sfSc;

  const slopeLines = [];
  for (let xi = -2.5; xi <= 2.5; xi += 0.5) {
    for (let yi = -2.5; yi <= 2.5; yi += 0.5) {
      const slope = yi; // dy/dx = y
      const len = 0.22;
      const denom = Math.sqrt(1 + slope * slope);
      const dx = len / denom, dy = slope * len / denom;
      slopeLines.push({ xi, yi, dx, dy });
    }
  }

  // Solution curves y = C*e^x for a few values of C
  const Cvals = [-1.2, -0.5, 0.3, 0.8, 1.4];
  const curvePts = Cvals.map(C => {
    const pts = [];
    for (let x = -2.6; x <= 2.1; x += 0.08) {
      const y = C * Math.exp(x);
      if (Math.abs(y) < 2.7) pts.push([sfTx(x), sfTy(y)]);
    }
    return pts;
  });

  // Right panel: 3 cards explaining the anatomy
  const cards = [
    { y: 20,  bg: '#EBF3FB', bc: '#2E75B6', title: 'The ODE  dy/dx = y', lines: ["Equation relating y and its derivative y'", 'Solution: a function y(x), not a number', 'This ODE says: rate of growth = current value'] },
    { y: 118, bg: '#f0fff4', bc: '#27ae60', title: 'General Solution  y = Ce^x', lines: ['C = arbitrary constant (family of curves)', 'One curve for each value of C', 'Slope field shows all solutions at once'] },
    { y: 216, bg: '#fff7ee', bc: '#e07b00', title: 'Particular Solution (IVP)', lines: ['Initial condition: y(0) = 1  →  C = 1', 'Selects ONE curve from the family', 'y(0)=1: C=1   y(0)=-0.5: C=-0.5'] },
    { y: 310, bg: '#fff0f0', bc: '#c0392b', title: 'Key Insight', lines: ['A DE is solved by integration, not algebra', 'The derivative tells you the slope at every point', 'Follow the slopes → trace the solution curve'] },
  ];

  const px0 = 460, panW = W - px0 - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>What is a Differential Equation? — Slope Fields and Solution Families</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Left panel */}
        <rect x={8} y={8} width={440} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={228} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Slope Field for  dy/dx = y</text>

        {/* Grid */}
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={sfTx(v)} y1={sfTy(-3)} x2={sfTx(v)} y2={sfTy(3)} stroke="#f0f0f0" strokeWidth="1" />
            <line x1={sfTx(-3)} y1={sfTy(v)} x2={sfTx(3)} y2={sfTy(v)} stroke="#f0f0f0" strokeWidth="1" />
            <text x={sfTx(v)} y={sfCy + 16} textAnchor="middle" fontSize="10" fill="#ccc">{v}</text>
            {v !== 0 && <text x={sfCx - 8} y={sfTy(v) + 4} textAnchor="end" fontSize="10" fill="#ccc">{v}</text>}
          </g>
        ))}

        {/* Axes */}
        <line x1={sfTx(-2.8)} y1={sfCy} x2={sfTx(2.8)} y2={sfCy} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={sfCx} y1={sfTy(-2.8)} x2={sfCx} y2={sfTy(2.8)} stroke="#1F3864" strokeWidth="1.5" />
        <polygon points={`${sfTx(2.8)},${sfCy} ${sfTx(2.8)-7},${sfCy-3} ${sfTx(2.8)-7},${sfCy+3}`} fill="#1F3864" />
        <polygon points={`${sfCx},${sfTy(2.8)} ${sfCx-3},${sfTy(2.8)+7} ${sfCx+3},${sfTy(2.8)+7}`} fill="#1F3864" />
        <text x={sfTx(2.8)+6} y={sfCy+4} fontSize="13" fill="#1F3864" fontWeight="700">x</text>
        <text x={sfCx+5} y={sfTy(2.8)-4} fontSize="13" fill="#1F3864" fontWeight="700">y</text>

        {/* Slope lines (field) */}
        {slopeLines.map(({ xi, yi, dx, dy }, i) => (
          <line key={i}
            x1={sfTx(xi - dx)} y1={sfTy(yi - dy)}
            x2={sfTx(xi + dx)} y2={sfTy(yi + dy)}
            stroke="#9ab8d8" strokeWidth="1.4"
          />
        ))}

        {/* Solution curves */}
        {curvePts.map((pts, ci) => {
          if (pts.length < 2) return null;
          const isHighlighted = ci === 3; // C=0.8 highlighted
          const d = pts.map((p, k) => `${k === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          return <path key={ci} d={d} fill="none" stroke={isHighlighted ? '#c0392b' : '#2E75B6'} strokeWidth={isHighlighted ? 2.8 : 1.6} opacity={isHighlighted ? 1 : 0.6} />;
        })}

        {/* IC point */}
        <circle cx={sfTx(0)} cy={sfTy(0.8)} r={6} fill="#c0392b" stroke="#fff" strokeWidth="2" />
        <text x={sfTx(0)+10} y={sfTy(0.8)-8} fontSize="12" fill="#c0392b" fontWeight="700">y(0)=0.8</text>
        <text x={sfTx(1.4)} y={sfTy(Cvals[3]*Math.exp(1.4))-10} fontSize="12" fill="#c0392b" fontWeight="700">y=0.8eˣ</text>

        {/* Right panel */}
        <rect x={px0} y={8} width={panW} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={px0 + panW/2} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Anatomy of a Solution</text>
        {cards.map(({ y, bg, bc, title, lines }) => (
          <g key={title}>
            <rect x={px0+8} y={y+36} width={panW-16} height={72} rx={3} fill={bg} stroke={bc} strokeWidth="1" />
            <text x={px0+16} y={y+52} fontSize="12" fill="#1F3864" fontWeight="700">{title}</text>
            {lines.map((ln, i) => (
              <text key={i} x={px0+16} y={y+68+i*16} fontSize="10.5" fill="#444" fontFamily="'Courier New',monospace">{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        A differential equation is an equation involving a function and its derivatives. The slope field plots the value of dy/dx at every point (x,y) — each tick shows the slope the solution must have there. Tracing through the tick marks traces out a solution curve. The general solution is a family of curves (one per value of C); an initial condition selects a single particular solution.
      </div>
    </div>
  );
}

// ── Diagram 2: Separation of Variables ────────────────────────────
function DiagramSeparable() {
  const W = 900, H = 370;

  // Left: step-by-step process diagram
  const steps = [
    { y: 30,  color: '#2E75B6', bg: '#EBF3FB', bc: '#2E75B6', step: '① Original ODE', eq: 'dy/dx = x·y' },
    { y: 100, color: '#27ae60', bg: '#f0fff4', bc: '#27ae60', step: '② Separate variables', eq: '(1/y) dy = x dx' },
    { y: 170, color: '#e07b00', bg: '#fff7ee', bc: '#e07b00', step: '③ Integrate both sides', eq: '∫(1/y) dy = ∫x dx' },
    { y: 240, color: '#9b59b6', bg: '#f8f0ff', bc: '#9b59b6', step: '④ Evaluate integrals', eq: 'ln|y| = x²/2 + C' },
    { y: 310, color: '#c0392b', bg: '#fff0f0', bc: '#c0392b', step: '⑤ Solve for y', eq: 'y = Ae^(x²/2),  A = ±eᶜ' },
  ];

  // Right: solution curves y = A*exp(x²/2) plotted
  const cx = 700, cy = 190, sc = 44;
  const tx = x => cx + x * sc;
  const ty = y => cy - y * sc;
  const Avals = [-1.2, -0.6, 0.4, 0.8, 1.3];
  const colors = ['#9b59b6','#2E75B6','#27ae60','#c0392b','#e07b00'];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Method 1 — Separation of Variables:  dy/dx = x·y  →  y = Ae^(x²/2)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Left: Steps */}
        <rect x={8} y={8} width={440} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={228} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Step-by-Step: Separation of Variables</text>

        {steps.map(({ y, bg, bc, step, eq }, i) => (
          <g key={i}>
            <rect x={20} y={y+28} width={408} height={58} rx={4} fill={bg} stroke={bc} strokeWidth="1.5" />
            <text x={34} y={y+46} fontSize="12" fill="#1F3864" fontWeight="700">{step}</text>
            <text x={34} y={y+66} fontSize="14" fill={bc} fontFamily="'Courier New',monospace" fontWeight="700">{eq}</text>
            {i < steps.length - 1 && (
              <text x={228} y={y+96} textAnchor="middle" fontSize="18" fill="#aaa">↓</text>
            )}
          </g>
        ))}

        {/* Right: solution curves */}
        <rect x={456} y={8} width={436} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={674} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Solution Family  y = Ae^(x²/2)</text>

        {/* Grid */}
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={tx(v)} y1={ty(-3.2)} x2={tx(v)} y2={ty(3.2)} stroke="#f0f0f0" strokeWidth="1" />
            <line x1={tx(-2.8)} y1={ty(v)} x2={tx(2.8)} y2={ty(v)} stroke="#f0f0f0" strokeWidth="1" />
            <text x={tx(v)} y={cy+15} textAnchor="middle" fontSize="10" fill="#ccc">{v}</text>
            {v!==0 && <text x={cx-8} y={ty(v)+4} textAnchor="end" fontSize="10" fill="#ccc">{v}</text>}
          </g>
        ))}

        {/* Axes */}
        <line x1={tx(-2.8)} y1={cy} x2={tx(2.8)} y2={cy} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={cx} y1={ty(-3.2)} x2={cx} y2={ty(3.2)} stroke="#1F3864" strokeWidth="1.5" />
        <text x={tx(2.8)+6} y={cy+4} fontSize="13" fill="#1F3864" fontWeight="700">x</text>
        <text x={cx+5} y={ty(3.2)-4} fontSize="13" fill="#1F3864" fontWeight="700">y</text>

        {/* Curves */}
        {Avals.map((A, ci) => {
          const pts = [];
          for (let x = -2.5; x <= 2.5; x += 0.06) {
            const y = A * Math.exp(x*x/2);
            if (Math.abs(y) <= 3.1) pts.push([tx(x), ty(y)]);
            else if (pts.length > 0) break;
          }
          if (pts.length < 2) return null;
          const d = pts.map((p,k) => `${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          return <path key={ci} d={d} fill="none" stroke={colors[ci]} strokeWidth="2" />;
        })}

        {/* Labels */}
        {Avals.map((A, ci) => {
          const xLabel = A > 0 ? 1.3 : -1.3;
          const yVal = A * Math.exp(xLabel*xLabel/2);
          if (Math.abs(yVal) > 3.0) return null;
          return <text key={ci} x={tx(xLabel)+6} y={ty(yVal)} fontSize="11" fill={colors[ci]} fontWeight="700">A={A}</text>;
        })}

        {/* Separatrix y=0 note */}
        <line x1={tx(-2.5)} y1={ty(0)} x2={tx(2.5)} y2={ty(0)} stroke="#888" strokeWidth="1.5" strokeDasharray="5,3" />
        <text x={tx(1.6)} y={ty(0)-8} fontSize="11" fill="#888" fontStyle="italic">y=0 (equilibrium)</text>

        {/* Key condition box */}
        <rect x={464} y={H-66} width={420} height={50} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1" />
        <text x={472} y={H-50} fontSize="12" fill="#1F3864" fontWeight="700">Separable condition:  dy/dx = f(x)·g(y)</text>
        <text x={472} y={H-33} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Rewrite as  (1/g(y))dy = f(x)dx  then integrate both sides</text>
      </svg>
      <div style={S.dCaption}>
        Separation of variables is the first and most intuitive ODE technique — it works whenever the equation can be written as a product of a function of x only and a function of y only. Move all y terms (including dy) to one side and all x terms (including dx) to the other, then integrate independently. The constant of integration C on one side absorbs into an arbitrary constant A in the final solution.
      </div>
    </div>
  );
}

// ── Diagram 3: Integrating Factor (First-Order Linear) ────────────
function DiagramIntegratingFactor() {
  const W = 900, H = 370;

  // Right: plot solutions to y' + y = sin(x)
  // Homogeneous: y_h = Ce^(-x)
  // Particular: y_p = (sin x - cos x)/2
  // General: y = Ce^(-x) + (sin x - cos x)/2
  const cx = 680, cy = 190, sc = 50;
  const tx = x => cx + x * sc;
  const ty = y => cy - y * sc;
  const yGen = (x, C) => C * Math.exp(-x) + (Math.sin(x) - Math.cos(x)) / 2;

  const Cvals = [2, 1, 0, -1, -2];
  const colors2 = ['#2E75B6','#27ae60','#c0392b','#e07b00','#9b59b6'];

  const steps2 = [
    { y:28,  bg:'#EBF3FB', bc:'#2E75B6', step:'① Standard form', eq:"y' + P(x)·y = Q(x)"},
    { y:96,  bg:'#EBF3FB', bc:'#2E75B6', step:'   This ODE', eq:"y' + y = sin(x)   →  P=1, Q=sin x"},
    { y:164, bg:'#f0fff4', bc:'#27ae60', step:'② Integrating factor  μ(x)', eq:'μ = e^(∫P dx) = e^(∫1 dx) = eˣ'},
    { y:232, bg:'#fff7ee', bc:'#e07b00', step:'③ Multiply through by μ', eq:"d/dx[eˣ·y] = eˣ·sin(x)"},
    { y:300, bg:'#f8f0ff', bc:'#9b59b6', step:'④ Integrate both sides', eq:'eˣ·y = ∫eˣ sin(x) dx  (by parts)'},
    { y:340, bg:'#fff0f0', bc:'#c0392b', step:'⑤ General solution', eq:'y = Ce⁻ˣ + (sin x − cos x)/2'},
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Method 2 — Integrating Factor:  y' + y = sin(x)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Left: steps */}
        <rect x={8} y={8} width={420} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={218} y={26} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Integrating Factor Method</text>

        {steps2.map(({ y, bg, bc, step, eq }, i) => (
          <g key={i}>
            <rect x={18} y={y+22} width={392} height={56} rx={3} fill={bg} stroke={bc} strokeWidth="1.5" />
            <text x={30} y={y+38} fontSize="11.5" fill="#1F3864" fontWeight="700">{step}</text>
            <text x={30} y={y+58} fontSize="12.5" fill={bc} fontFamily="'Courier New',monospace" fontWeight="700">{eq}</text>
          </g>
        ))}

        {/* Right: solution curves */}
        <rect x={436} y={8} width={456} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={664} y={26} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Solution Family  y = Ce⁻ˣ + (sin x−cos x)/2</text>

        {/* Grid */}
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={tx(v)} y1={ty(-3)} x2={tx(v)} y2={ty(3)} stroke="#f0f0f0" strokeWidth="1" />
            <line x1={tx(-2.8)} y1={ty(v)} x2={tx(2.8)} y2={ty(v)} stroke="#f0f0f0" strokeWidth="1" />
            <text x={tx(v)} y={cy+15} textAnchor="middle" fontSize="10" fill="#ccc">{v}</text>
            {v!==0 && <text x={cx-8} y={ty(v)+4} textAnchor="end" fontSize="10" fill="#ccc">{v}</text>}
          </g>
        ))}

        <line x1={tx(-2.8)} y1={cy} x2={tx(2.8)} y2={cy} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={cx} y1={ty(-3)} x2={cx} y2={ty(3)} stroke="#1F3864" strokeWidth="1.5" />
        <text x={tx(2.8)+6} y={cy+4} fontSize="13" fill="#1F3864" fontWeight="700">x</text>
        <text x={cx+5} y={ty(3)-4} fontSize="13" fill="#1F3864" fontWeight="700">y</text>

        {/* Particular solution */}
        {(() => {
          const pts = [];
          for (let x=-2.8; x<=2.8; x+=0.06) {
            const y=(Math.sin(x)-Math.cos(x))/2;
            if(Math.abs(y)<=2.9) pts.push([tx(x),ty(y)]);
          }
          const d=pts.map((p,k)=>`${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          return <path d={d} fill="none" stroke="#888" strokeWidth="2" strokeDasharray="6,3" />;
        })()}
        <text x={tx(2.2)} y={ty(yGen(2.2,0))-12} fontSize="11" fill="#888" fontStyle="italic">y_p (C=0)</text>

        {/* General curves */}
        {Cvals.map((C, ci) => {
          if (C === 0) return null;
          const pts = [];
          for (let x = -1.2; x <= 2.8; x += 0.06) {
            const y = yGen(x, C);
            if (Math.abs(y) <= 2.9) pts.push([tx(x), ty(y)]);
            else if (pts.length > 0) break;
          }
          if (pts.length < 2) return null;
          const d = pts.map((p,k) => `${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          return <path key={ci} d={d} fill="none" stroke={colors2[ci]} strokeWidth="1.8" />;
        })}

        {/* Labels */}
        {Cvals.filter(C=>C!==0).map((C,ci)=>{
          const xL=C>0?-0.8:0.2;
          const yV=yGen(xL,C);
          if(Math.abs(yV)>2.8) return null;
          return <text key={ci} x={tx(xL)-26} y={ty(yV)} fontSize="10" fill={colors2[ci]} fontWeight="700">C={C}</text>;
        })}

        {/* Transient decay note */}
        <rect x={444} y={H-70} width={440} height={54} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1" />
        <text x={452} y={H-54} fontSize="12" fill="#1F3864" fontWeight="700">Transient vs. Steady State</text>
        <text x={452} y={H-37} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Ce⁻ˣ → 0 as x→∞  (transient, dies out)</text>
        <text x={452} y={H-20} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">y_p = (sin x−cos x)/2  (steady-state oscillation)</text>
      </svg>
      <div style={S.dCaption}>
        The integrating factor method handles any first-order linear ODE of the form y' + P(x)y = Q(x). Multiplying by μ = e^(∫P dx) turns the left side into an exact derivative d/dx[μy], making it directly integrable. The general solution always splits into a transient (homogeneous) part that decays and a steady-state (particular) part that matches the forcing — a pattern that appears in every linear system in engineering.
      </div>
    </div>
  );
}

// ── Diagram 4: Second-Order Constant Coefficient ODE ──────────────
function DiagramSecondOrder() {
  const W = 900, H = 400;

  // Three cases: overdamped, critically damped, underdamped
  // my'' + cy' + ky = 0, normalised m=1, k=1
  // char eq: r² + 2ζr + 1 = 0   (ζ = c/2)
  const cases = [
    { label: 'Overdamped', ζ: 1.8, color: '#2E75B6', desc: 'ζ > 1  →  two real roots\nr = −ζ ± √(ζ²−1)' },
    { label: 'Critically Damped', ζ: 1.0, color: '#27ae60', desc: 'ζ = 1  →  one repeated root\nr = −1 (double)' },
    { label: 'Underdamped', ζ: 0.25, color: '#c0392b', desc: 'ζ < 1  →  complex roots\nr = −ζ ± iω_d' },
  ];

  const plotY = (ζ, t) => {
    const disc = ζ*ζ - 1;
    if (disc > 0) {
      const r1 = -ζ + Math.sqrt(disc), r2 = -ζ - Math.sqrt(disc);
      return Math.exp(r1*t) - Math.exp(r2*t); // difference, passes through 0 at t=0 with slope
    } else if (Math.abs(disc) < 1e-8) {
      return t * Math.exp(-t);
    } else {
      const wd = Math.sqrt(1 - ζ*ζ);
      return Math.exp(-ζ*t) * Math.sin(wd*t) / wd;
    }
  };

  // Normalise peak to 1 for each case
  const peaks = cases.map(({ζ}) => {
    let max = 0;
    for(let t=0;t<=12;t+=0.05) max = Math.max(max, Math.abs(plotY(ζ,t)));
    return max || 1;
  });

  const cx = 228, cy = 220, scX = 46, scY = 140;
  const tx = t => cx + t*scX - 2*scX; // start at t=0 near left
  const ty = (y, norm) => cy - y/norm * scY;

  // Panel layout: left graph, right anatomy cards
  const panelW = 440;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Second-Order ODEs — The Three Damping Cases (Mass-Spring-Damper)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Left: plot of all three cases */}
        <rect x={8} y={8} width={panelW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={panelW/2+8} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">y'' + 2ζy' + y = 0,  y(0)=0, y'(0)=1</text>

        {/* Grid */}
        {[0,2,4,6,8,10].map(t=>(
          <g key={t}>
            <line x1={tx(t)} y1={40} x2={tx(t)} y2={H-40} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={tx(t)} y={H-26} textAnchor="middle" fontSize="10" fill="#ccc">{t}</text>
          </g>
        ))}
        {[-1,-0.5,0,0.5,1].map(v=>(
          <g key={v}>
            <line x1={tx(0)} y1={ty(v,1)} x2={tx(10)} y2={ty(v,1)} stroke="#f0f0f0" strokeWidth="1"/>
            {v!==0 && <text x={tx(0)-6} y={ty(v,1)+4} textAnchor="end" fontSize="10" fill="#ccc">{v}</text>}
          </g>
        ))}

        {/* Axes */}
        <line x1={tx(0)} y1={cy} x2={tx(10.2)} y2={cy} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={tx(0)} y1={40} x2={tx(0)} y2={H-38} stroke="#1F3864" strokeWidth="1.5"/>
        <text x={tx(10.2)+6} y={cy+4} fontSize="12" fill="#1F3864" fontWeight="700">t</text>
        <text x={tx(0)+4} y={38} fontSize="12" fill="#1F3864" fontWeight="700">y</text>

        {/* Zero line label */}
        <text x={tx(0)-6} y={cy+4} textAnchor="end" fontSize="10" fill="#ccc">0</text>

        {/* Curves */}
        {cases.map(({ζ, color}, ci) => {
          const pts = [];
          for(let t=0;t<=10;t+=0.05){
            const y=plotY(ζ,t);
            const yN=y/peaks[ci];
            if(Math.abs(yN)<=1.2) pts.push([tx(t),ty(yN,1)]);
          }
          const d=pts.map((p,k)=>`${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          return <path key={ci} d={d} fill="none" stroke={color} strokeWidth="2.5"/>;
        })}

        {/* Equilibrium line */}
        <line x1={tx(0)} y1={cy} x2={tx(10)} y2={cy} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3"/>

        {/* Labels on curves */}
        {cases.map(({ζ,color,label},ci)=>{
          const tLabel=ci===2?3:1.5;
          const y=plotY(ζ,tLabel)/peaks[ci];
          if(Math.abs(y)>1.1) return null;
          return <text key={ci} x={tx(tLabel)+6} y={ty(y,1)-8} fontSize="11" fill={color} fontWeight="700">{label}</text>;
        })}

        {/* Time axis label */}
        <text x={tx(5)} y={H-12} textAnchor="middle" fontSize="11" fill="#555" fontStyle="italic">time →  (all responses start at y(0)=0, y'(0)=1)</text>

        {/* Right: anatomy cards */}
        <rect x={456} y={8} width={436} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={674} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Characteristic Equation &amp; Solution Forms</text>

        {/* Char equation box */}
        <rect x={464} y={38} width={420} height={60} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={472} y={56} fontSize="13" fill="#1F3864" fontWeight="700">ay'' + by' + cy = 0</text>
        <text x={472} y={74} fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace">Assume y = eʳˣ  →  ar² + br + c = 0</text>
        <text x={472} y={90} fontSize="11" fill="#555">Characteristic equation — solve for r</text>

        {cases.map(({label,color,desc},ci)=>{
          const yOff=114+ci*88;
          const sols=['y = C₁e^(r₁x) + C₂e^(r₂x)\nr₁≠r₂, both real and negative → slow decay',
                      'y = (C₁ + C₂x)e^(rx)\nRepeated root → fastest return without oscillation',
                      'y = e^(−ζx)(C₁cos(ω_d x)+C₂sin(ω_d x))\nω_d=√(1−ζ²) — oscillates while decaying'];
          const bgs=['#EBF3FB','#f0fff4','#fff0f0'];
          const bcs=['#2E75B6','#27ae60','#c0392b'];
          return (
            <g key={ci}>
              <rect x={464} y={yOff} width={420} height={78} rx={3} fill={bgs[ci]} stroke={bcs[ci]} strokeWidth="1.5"/>
              <text x={472} y={yOff+16} fontSize="12" fill={color} fontWeight="700">{label}  ({desc.split('\n')[0]})</text>
              <text x={472} y={yOff+32} fontSize="10.5" fill="#555" fontFamily="'Courier New',monospace">{desc.split('\n')[1]}</text>
              {sols[ci].split('\n').map((ln,i)=>(
                <text key={i} x={472} y={yOff+50+i*16} fontSize="10.5" fill="#444" fontFamily="'Courier New',monospace">{ln}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        The second-order linear constant-coefficient ODE models the mass-spring-damper and appears throughout mechanical, electrical, and control engineering. The key step is assuming y = eʳˣ, which converts the ODE into an algebraic characteristic equation. The discriminant of that quadratic determines whether the system oscillates (underdamped), returns directly (critically damped), or decays sluggishly (overdamped). All engineering resonance and vibration analysis is built on this foundation.
      </div>
    </div>
  );
}

// ── Diagram 5: Particular Solutions & Undetermined Coefficients ───
function DiagramParticularSolution() {
  const W = 900, H = 360;

  // Right: plot of full solution y = y_h + y_p
  // y'' + 2(0.2)y' + y = cos(0.8t)  (near resonance)
  const ζ=0.2, ω=0.8;
  const ωd=Math.sqrt(1-ζ*ζ);
  // Particular solution amplitude & phase for y''+2ζy'+ω₀²y=cos(ωt), ω₀=1
  const denom=Math.sqrt((1-ω*ω)**2+(2*ζ*ω)**2);
  const phi=Math.atan2(2*ζ*ω,1-ω*ω);
  const Ap=1/denom;

  const yFull=(t,C1,C2)=>
    Math.exp(-ζ*t)*(C1*Math.cos(ωd*t)+C2*Math.sin(ωd*t))
    + Ap*Math.cos(ω*t-phi);

  // Apply y(0)=0, y'(0)=0
  const C1=-Ap*Math.cos(-phi);
  const C2s=(-ζ*C1+Ap*ω*Math.sin(-phi))/ωd;

  const cx2=680, cy2=195, scX2=36, scY2=95;
  const tx2=t=>440+t*scX2;
  const ty2=y=>cy2-y*scY2;

  const tableRows = [
    ['y_p guess for e^(αx)',         'Try y_p = Ae^(αx)'],
    ['y_p guess for sin(βx) or cos(βx)', 'Try y_p = A cos(βx) + B sin(βx)\n(include BOTH even if only one appears)'],
    ['y_p guess for polynomial xⁿ',  'Try y_p = Aₙxⁿ + … + A₁x + A₀\n(full polynomial of same degree)'],
    ['y_p guess for product',         'Try y_p = product of individual guesses\ne.g. e^x·sin x → e^x(A cos x + B sin x)'],
    ['Modification rule',             'If guess duplicates y_h term, multiply by x\nIf still duplicates, multiply by x² etc.'],
    ['Superposition',                 'If forcing = f₁ + f₂, solve each separately\ny_p = y_p1 + y_p2'],
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Particular Solutions — Method of Undetermined Coefficients &amp; Resonance</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Left: guess table */}
        <rect x={8} y={8} width={424} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={220} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Undetermined Coefficients — Guess Table</text>

        {/* Header */}
        <rect x={16} y={36} width={408} height={26} rx={2} fill="#2E75B6"/>
        <text x={130} y={54} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">Forcing f(x)</text>
        <text x={310} y={54} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">Trial Particular Solution y_p</text>

        {tableRows.map(([forcing,trial],i)=>{
          const yR=62+i*47;
          const bg=i%2===0?'#f5f9ff':'#fff';
          return (
            <g key={i}>
              <rect x={16} y={yR} width={408} height={44} rx={0} fill={bg} stroke="#ccd9ea" strokeWidth="0.5"/>
              <line x1={16} y1={yR} x2={424} y2={yR} stroke="#ccd9ea" strokeWidth="0.5"/>
              <line x1={228} y1={yR} x2={228} y2={yR+44} stroke="#ccd9ea" strokeWidth="0.5"/>
              <text x={24} y={yR+15} fontSize="11" fill="#1F3864" fontWeight="700">{forcing}</text>
              {trial.split('\n').map((ln,k)=>(
                <text key={k} x={234} y={yR+15+k*16} fontSize="10.5" fill="#2E75B6" fontFamily="'Courier New',monospace">{ln}</text>
              ))}
            </g>
          );
        })}

        {/* Right: resonance plot */}
        <rect x={436} y={8} width={456} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={664} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Near-Resonance: y''+0.4y'+y = cos(0.8t), y(0)=y'(0)=0</text>

        {/* Grid */}
        {[0,5,10,15].map(t=>(
          <g key={t}>
            <line x1={tx2(t)} y1={40} x2={tx2(t)} y2={H-40} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={tx2(t)} y={H-24} textAnchor="middle" fontSize="10" fill="#ccc">{t}</text>
          </g>
        ))}
        {[-2,-1,0,1,2].map(v=>(
          <g key={v}>
            <line x1={tx2(0)} y1={ty2(v)} x2={tx2(15.5)} y2={ty2(v)} stroke="#f0f0f0" strokeWidth="1"/>
            {v!==0 && <text x={tx2(0)-6} y={ty2(v)+4} textAnchor="end" fontSize="10" fill="#ccc">{v}</text>}
          </g>
        ))}

        <line x1={tx2(0)} y1={cy2} x2={tx2(15.5)} y2={cy2} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={tx2(0)} y1={40} x2={tx2(0)} y2={H-38} stroke="#1F3864" strokeWidth="1.5"/>
        <text x={tx2(15.5)+5} y={cy2+4} fontSize="12" fill="#1F3864" fontWeight="700">t</text>
        <text x={tx2(0)+4} y={38} fontSize="12" fill="#1F3864" fontWeight="700">y</text>
        <text x={tx2(0)-6} y={cy2+4} textAnchor="end" fontSize="10" fill="#ccc">0</text>

        {/* Particular solution alone */}
        {(()=>{
          const pts=[];
          for(let t=0;t<=15.5;t+=0.08){
            const y=Ap*Math.cos(ω*t-phi);
            if(Math.abs(y)<=2.5) pts.push([tx2(t),ty2(y)]);
          }
          const d=pts.map((p,k)=>`${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          return <path d={d} fill="none" stroke="#aaa" strokeWidth="1.5" strokeDasharray="5,3"/>;
        })()}
        <text x={tx2(12)} y={ty2(Ap*Math.cos(ω*12-phi))-12} fontSize="11" fill="#888" fontStyle="italic">y_p (steady state)</text>

        {/* Full solution */}
        {(()=>{
          const pts=[];
          for(let t=0;t<=15.5;t+=0.06){
            const y=yFull(t,C1,C2s);
            if(Math.abs(y)<=2.5) pts.push([tx2(t),ty2(y)]);
          }
          const d=pts.map((p,k)=>`${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          return <path d={d} fill="none" stroke="#c0392b" strokeWidth="2.5"/>;
        })()}
        <text x={tx2(2.5)} y={ty2(yFull(2.5,C1,C2s))-12} fontSize="11" fill="#c0392b" fontWeight="700">y = y_h + y_p</text>

        {/* Transient annotation */}
        <rect x={444} y={H-72} width={440} height={56} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={452} y={H-56} fontSize="12" fill="#1F3864" fontWeight="700">Resonance occurs when ω → ω₀  (forcing freq = natural freq)</text>
        <text x={452} y={H-39} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">At exact resonance (ζ=0): amplitude → ∞ (unbounded growth)</text>
        <text x={452} y={H-22} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">With damping: amplitude peaks at finite value (Ap = 1/2ζ at ω=ω₀)</text>
      </svg>
      <div style={S.dCaption}>
        For a non-homogeneous ODE, the general solution is y = y_h + y_p. The homogeneous part y_h solves the equation with zero right-hand side and contains the arbitrary constants. The particular solution y_p matches the forcing. Undetermined coefficients guesses y_p based on the form of the forcing function, substitutes it in, and solves for the unknown coefficients by matching terms. Near resonance the transient and steady-state parts initially interfere, producing beating before the transient decays away.
      </div>
    </div>
  );
}

// ── Diagram 6: Classification Tree ───────────────────────────────
function DiagramClassification() {
  const W = 900, H = 340;

  const node = (x, y, w, h, bg, bc, label, sub) => (
    <g>
      <rect x={x-w/2} y={y-h/2} width={w} height={h} rx={5} fill={bg} stroke={bc} strokeWidth="1.8"/>
      <text x={x} y={sub ? y-4 : y+5} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">{label}</text>
      {sub && <text x={x} y={y+13} textAnchor="middle" fontSize="10" fill="#555" fontFamily="'Courier New',monospace">{sub}</text>}
    </g>
  );

  const edge = (x1,y1,x2,y2,label,lx,ly) => (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#aaa" strokeWidth="1.5"/>
      <polygon points={`${x2},${y2} ${x2-5*Math.cos(Math.atan2(y2-y1,x2-x1)-0.4)},${y2-5*Math.sin(Math.atan2(y2-y1,x2-x1)-0.4)} ${x2-5*Math.cos(Math.atan2(y2-y1,x2-x1)+0.4)},${y2-5*Math.sin(Math.atan2(y2-y1,x2-x1)+0.4)}`} fill="#aaa"/>
      {label && <text x={lx} y={ly} fontSize="10" fill="#777" fontStyle="italic">{label}</text>}
    </g>
  );

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>ODE Classification — Order, Linearity, and Solution Strategy</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Root */}
        {node(450,32,200,32,'#1F3864','#1F3864','Differential Equation',null)}
        {/* Override text color for dark bg */}
        <text x={450} y={37} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">Differential Equation</text>

        {edge(450,48,250,90,'ODE',192,76)}
        {edge(450,48,650,90,'PDE',672,76)}

        {node(250,108,180,30,'#EBF3FB','#2E75B6','ODE (one variable)',null)}
        {node(650,108,180,30,'#fff7ee','#e07b00','PDE (multiple vars)',null)}

        {edge(250,123,140,163,'1st order',72,152)}
        {edge(250,123,360,163,'2nd order +',338,152)}

        {node(140,180,140,30,'#f0fff4','#27ae60','First Order',null)}
        {node(360,180,160,30,'#f0fff4','#27ae60','Second Order +',null)}

        {edge(140,195,74,236,'separable',26,224)}
        {edge(140,195,140,236,'linear',128,224)}
        {edge(140,195,206,236,'exact',196,224)}

        {node(74,252,108,28,'#EBF3FB','#2E75B6','Separable','g(y)dy=f(x)dx')}
        {node(200,252,108,28,'#EBF3FB','#2E75B6','Linear IF','y\'+Py=Q')}

        {edge(360,195,290,236,'const coeff',246,224)}
        {edge(360,195,430,236,'variable coeff',400,224)}

        {node(290,252,120,28,'#fff0f0','#c0392b','Char. Equation','ar²+br+c=0')}
        {node(430,252,120,28,'#fff0f0','#c0392b','Power Series','y=Σaₙxⁿ')}

        {/* Nonhomogeneous branch */}
        {edge(360,195,530,236,'nonhomogeneous',470,207)}
        {node(530,252,120,28,'#f8f0ff','#9b59b6','y_h + y_p','Undetermined coeff')}

        {/* PDE branch */}
        {edge(650,123,650,163,null,null,null)}
        {node(650,180,170,28,'#fff7ee','#e07b00','Classify PDE type',null)}
        {edge(650,195,580,236,'elliptic',544,224)}
        {edge(650,195,650,236,'parabolic',638,224)}
        {edge(650,195,720,236,'hyperbolic',700,224)}
        {node(565,252,90,28,'#fff7ee','#e07b00','Laplace','∇²u=0')}
        {node(650,252,90,28,'#fff7ee','#e07b00','Heat','u_t=α∇²u')}
        {node(730,252,90,28,'#fff7ee','#e07b00','Wave','u_tt=c²∇²u')}

        {/* Bottom note */}
        <rect x={14} y={H-30} width={872} height={22} rx={3} fill="#1F3864" fillOpacity="0.05"/>
        <text x={450} y={H-15} textAnchor="middle" fontSize="11" fill="#555" fontStyle="italic">
          This page covers ODEs. PDEs (Heat, Wave, Laplace) require separation of variables in multiple dimensions and are covered in their own sections.
        </text>
      </svg>
      <div style={S.dCaption}>
        Choosing the right solution method starts with classifying the equation. First ask: is it an ODE (one independent variable) or PDE (multiple)? Then: what order? Then: linear or nonlinear? Then: homogeneous or non-homogeneous? Each branch of the tree leads to a specific technique. The vast majority of ODEs encountered in undergraduate engineering fall into the left two branches of this tree.
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────
export default function DifferentialEquations() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Differential Equations</h1>
      <p style={S.p}>
        A differential equation (DE) is any equation that contains an unknown function and one or more of its derivatives. Where algebra asks "find the number x", a differential equation asks "find the function y(x)". The solution is not a value — it is a curve. This makes DEs the natural language for describing anything that changes: the position of a mass on a spring, the temperature across a rod, the charge on a capacitor, or the deflection of a beam under load.
      </p>
      <p style={S.p}>
        This page focuses on ordinary differential equations (ODEs) from a calculus perspective — building directly from integration, connecting each method back to the fundamental theorem of calculus, and establishing the intuition before the formalism.
      </p>

      <InfoBox title="The bridge from calculus: a DE is an equation you solve by integrating" items={[
        "dy/dx = f(x)  →  solve by direct integration: y = ∫f(x)dx + C  (trivial ODE)",
        "dy/dx = g(y)  →  solve by separating and integrating both sides",
        "dy/dx = f(x)·g(y)  →  separable; divide by g(y), integrate both sides",
        "y' + P(x)y = Q(x)  →  integrating factor μ=e^∫P dx turns it into d/dx[μy]=μQ",
        "ay'' + by' + cy = 0  →  characteristic equation gives r; y=eʳˣ is the building block",
        "Every first-order linear ODE can be solved exactly; higher-order often requires numerical methods",
      ]}/>

      <h2 style={S.h2}>What is a Differential Equation?</h2>
      <DiagramWhatIsODE/>
      <Table2 rows={[
        ["ODE vs PDE","ODE: unknown depends on one variable — y(x) or y(t)\nPDE: unknown depends on multiple variables — u(x,t)"],
        ["Order","Highest derivative present\n1st order: y';  2nd order: y'';  nth order: y⁽ⁿ⁾"],
        ["Linear ODE","All y and its derivatives appear to the first power; no products\naₙ(x)y⁽ⁿ⁾ + … + a₁(x)y' + a₀(x)y = f(x)"],
        ["Nonlinear ODE","Contains y², (y')², y·y', sin(y), etc.\nMuch harder — often only numerical solutions exist"],
        ["Homogeneous","Right-hand side = 0:  Ly = 0\nSolution space is a vector space"],
        ["Non-homogeneous","Right-hand side ≠ 0:  Ly = f(x)\nGeneral solution = y_h + y_p"],
        ["General solution","Contains arbitrary constants (one per order)\nRepresents the whole family of solution curves"],
        ["Particular solution","General solution with constants fixed by initial/boundary conditions\nSelects one curve from the family"],
        ["Initial Value Problem (IVP)","Conditions specified at a single point x₀\ny(x₀)=y₀, y'(x₀)=y₁, … (as many as the order)"],
        ["Slope field","Grid of short line segments showing dy/dx at each (x,y)\nVisualises all solutions simultaneously without solving"],
      ]}/>

      <h2 style={S.h2}>Separation of Variables</h2>
      <DiagramSeparable/>
      <Table2 rows={[
        ["Applicable when","dy/dx = f(x)·g(y)  — right side factors into x-only × y-only\nCovers: exponential growth/decay, logistic, Newton's cooling"],
        ["Procedure","1. Rewrite as (1/g(y))dy = f(x)dx\n2. Integrate both sides independently\n3. Apply initial condition to find C"],
        ["Exponential growth","dy/dt = ky  →  y = Ce^(kt)\nk>0: growth (population);  k<0: decay (radioactive, cooling)"],
        ["Logistic equation","dy/dt = ky(1−y/L)  →  S-curve solution\nL = carrying capacity; used in population and epidemic models"],
        ["Newton's cooling","dT/dt = −k(T − T_env)\nSeparable → T(t) = T_env + (T₀−T_env)e^(−kt)"],
        ["Free-fall with drag","mv' = mg − bv  →  separable\nTerminal velocity v_∞ = mg/b when v'=0"],
        ["Constant of integration","Always generates +C on one side (absorbed into ±eᶜ = A)\nA is determined by the initial condition, not by the integral"],
        ["Equilibrium solutions","g(y)=0 gives constant solutions y=const\nThese cannot be reached from non-equilibrium initial conditions"],
      ]}/>

      <h2 style={S.h2}>First-Order Linear — Integrating Factor</h2>
      <DiagramIntegratingFactor/>
      <Table2 rows={[
        ["Standard form","y' + P(x)·y = Q(x)\nIf not in this form: divide through by the leading coefficient"],
        ["Integrating factor","μ(x) = e^(∫P(x)dx)\nNo constant needed in this integral (it cancels)"],
        ["Why it works","μy' + μPy = d/dx[μy]  by the product rule\nLeft side becomes an exact derivative — directly integrable"],
        ["After multiplying","d/dx[μy] = μQ(x)\nIntegrate both sides: μy = ∫μQ dx + C"],
        ["General solution","y = (1/μ)·[∫μQ dx + C]\nAlways works for any first-order linear ODE"],
        ["Transient + steady state","y_h = C/μ  (dies out if P>0, i.e. μ→∞)\ny_p = particular integral — persists as t→∞"],
        ["RC circuit analogy","V_c' + V_c/(RC) = V_in(t)/RC\nSame form: P=1/RC, Q=V_in/RC; τ=RC is the time constant"],
        ["Bernoulli equation","y' + P(x)y = Q(x)yⁿ  (nonlinear)\nSubstitute v=y^(1-n) to reduce to linear — then use IF"],
      ]}/>

      <h2 style={S.h2}>Second-Order Constant Coefficient ODEs</h2>
      <DiagramSecondOrder/>
      <Table2 rows={[
        ["General form","ay'' + by' + cy = 0\nModels: mass-spring-damper, RLC circuit, beam deflection"],
        ["Characteristic equation","Assume y=eʳˣ → ar²+br+c=0\nSolve quadratic for r"],
        ["Case 1: Two real roots r₁≠r₂","y = C₁e^(r₁x) + C₂e^(r₂x)\nOverdamped if both roots negative"],
        ["Case 2: Repeated root r","y = (C₁ + C₂x)e^(rx)\nCritically damped — fastest return to equilibrium"],
        ["Case 3: Complex roots α±iβ","y = e^(αx)[C₁cos(βx) + C₂sin(βx)]\nUnderdamped — oscillates with decaying amplitude"],
        ["Damping ratio ζ","ζ = b/(2√(ac))\nζ>1: overdamped;  ζ=1: critical;  ζ<1: underdamped"],
        ["Natural frequency ω₀","ω₀ = √(c/a)  (rad/s)\nFrequency of undamped oscillation"],
        ["Damped frequency ω_d","ω_d = ω₀√(1−ζ²)\nActual oscillation frequency when damping present"],
        ["Applying ICs","Two conditions needed (e.g. y(0), y'(0))\nSolve 2×2 linear system for C₁ and C₂"],
        ["Wronskian","W(y₁,y₂) = y₁y₂' − y₂y₁'\nW≠0 confirms y₁,y₂ are linearly independent (valid general solution)"],
      ]}/>

      <h2 style={S.h2}>Non-Homogeneous ODEs & Particular Solutions</h2>
      <DiagramParticularSolution/>
      <Table2 rows={[
        ["Structure of general solution","y = y_h + y_p\ny_h: solves homogeneous equation (contains constants)\ny_p: any one solution of the full equation"],
        ["Undetermined coefficients","Guess y_p based on form of f(x); substitute into ODE\nMatch coefficients of like terms to solve for unknowns"],
        ["Variation of parameters","y_p = y₁∫(y₂f/W)dx − y₂∫(y₁f/W)dx\nWorks for any f(x); W = Wronskian of y₁,y₂"],
        ["Superposition principle","If Ly=f₁ and Ly=f₂, then Ly=f₁+f₂ solved by y_p1+y_p2\nOnly valid for linear equations"],
        ["Resonance","Occurs when forcing frequency = natural frequency ω₀\nAt exact resonance with no damping: amplitude grows without bound (xsin x terms)"],
        ["Practical resonance","With damping, maximum response at ω = ω₀√(1−2ζ²)\nAmplitude = 1/(2ζ√(1−ζ²)) at this peak"],
        ["Beating","Near-resonance: amplitude oscillates slowly between 0 and max\nFrequency of envelope = |ω−ω₀|/2"],
        ["Forced RLC circuit","LI'' + RI' + (1/C)I = V₀ω cos(ωt)\nSame structure: transient (y_h) + steady-state (y_p)"],
      ]}/>

      <h2 style={S.h2}>Classification & Solution Strategy</h2>
      <DiagramClassification/>
      <Table2 rows={[
        ["Step 1: ODE or PDE?","Count independent variables\nOne (x or t only) → ODE;  two or more (x and t) → PDE"],
        ["Step 2: Order?","Find highest derivative\nDetermines number of initial/boundary conditions needed"],
        ["Step 3: Linear?","Can you write it as Σ aₙ(x) y⁽ⁿ⁾ = f(x)?\nLinear → exact methods;  nonlinear → often numerical only"],
        ["Step 4: Homogeneous?","Is f(x) = 0 on the right side?\nHomogeneous → char. equation only;  non-homogeneous → y_h + y_p"],
        ["Step 5: Which method?","1st order separable → separation\n1st order linear → integrating factor\n2nd order const coeff → characteristic equation\nOther → variation of parameters / numerical"],
        ["Existence & uniqueness","Picard's theorem: if f and ∂f/∂y continuous near (x₀,y₀)\n→ unique solution through that point exists"],
        ["Numerical methods","Euler: yₙ₊₁ = yₙ + h·f(xₙ,yₙ)\nRunge-Kutta (RK4): 4-stage weighted average — much more accurate"],
        ["Engineering perspective","Most real systems → nonlinear PDEs\nLinearise around operating point → solve linear ODE → valid near that point"],
      ]}/>

      <InfoBox title="Key Takeaway — Connecting DEs Back to Calculus" items={[
        "Every ODE solution involves integration — the DE tells you the derivative, you integrate to get y",
        "dy/dx = f(x) alone? → Direct integration: y = ∫f(x)dx  (FTC applied directly)",
        "Separable? → Separate and integrate each side independently",
        "First-order linear? → Integrating factor converts it to d/dx[μy] = μQ, then integrate",
        "Second-order? → Characteristic equation gives eʳˣ building blocks; ICs fix constants",
        "Non-homogeneous? → y = y_h + y_p; particular solution found by guessing form of forcing",
        "The arbitrary constant C always appears because integration produces a family of antiderivatives",
        "Initial conditions are the calculus equivalent of evaluating a definite integral — they pin down C",
      ]}/>
    </div>
  );
}